import { useState, useEffect } from "react";
import { BookOpen, Clock, ChevronRight, Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  phishing: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20" },
  network: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/20" },
  ransomware: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20" },
  passwords: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20" },
  "ai-security": { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/20" },
};

const DIFFICULTY_COLORS = {
  beginner: "text-emerald-400",
  intermediate: "text-amber-400",
  advanced: "text-red-400",
};

export default function EducationPage() {
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API}/education/articles`);
        setArticles(resp.data.articles || []);
      } catch {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = articles.filter(
    (a) => a.title.toLowerCase().includes(search.toLowerCase()) || a.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="education-page">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-500" strokeWidth={1.5} />
            <span className="text-xl font-black tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>ZYBERCURE</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="text-zinc-400 hover:text-zinc-50 text-sm rounded-sm">Sign In</Button></Link>
            <Link to="/dashboard"><Button className="bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-sm font-semibold">Dashboard</Button></Link>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-10">
          <span className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-bold">LEARN</span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-3 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Cybersecurity Education Hub
          </h1>
          <p className="text-base text-zinc-400 max-w-xl">Expert guides and articles to keep you protected in the digital world.</p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles..."
            className="bg-zinc-900 border-zinc-800 rounded-sm h-11 pl-10 text-zinc-50 placeholder:text-zinc-600"
            data-testid="education-search"
          />
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading articles...</div>
        ) : (
          <>
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              {["All", "phishing", "network", "ransomware", "passwords", "ai-security"].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSearch(cat === "All" ? "" : cat)}
                  className={`text-xs px-3 py-1.5 rounded-sm font-semibold tracking-wide transition-colors ${
                    (cat === "All" && !search) || search === cat
                      ? "bg-blue-600 text-white"
                      : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-800 hover:text-zinc-200"
                  }`}
                  data-testid={`category-filter-${cat.toLowerCase()}`}
                >
                  {cat === "All" ? "All Topics" : cat.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((article, i) => {
                const cat = CATEGORY_COLORS[article.category] || CATEGORY_COLORS.network;
                return (
                  <div
                    key={article.article_id || i}
                    className="trace-border rounded-sm bg-zinc-900 p-6 group hover:scale-[1.01] hover:border-zinc-700 transition-all duration-200"
                    data-testid={`article-card-${i}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-sm ${cat.bg} ${cat.text} border ${cat.border}`}>
                        {article.category}
                      </span>
                      <span className={`text-[10px] uppercase tracking-[0.15em] font-bold ${DIFFICULTY_COLORS[article.difficulty] || "text-zinc-400"}`}>
                        {article.difficulty}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold tracking-tight text-zinc-50 mb-2 group-hover:text-blue-400 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                      {article.title}
                    </h3>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-4">{article.summary}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-zinc-500">
                        <Clock className="w-3 h-3" /> {article.read_time} min read
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {article.tags?.slice(0, 2).map((tag) => (
                          <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded-sm">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <BookOpen className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
                <p className="text-sm">No articles found matching your search.</p>
              </div>
            )}
          </>
        )}

        {/* CTA */}
        <div className="mt-16 trace-border rounded-sm bg-zinc-900 p-8 text-center">
          <h3 className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Want Personalized Security Guidance?</h3>
          <p className="text-sm text-zinc-400 mb-4">Chat with ZyberGuard AI for instant, expert cybersecurity advice tailored to your situation.</p>
          <Link to="/register">
            <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold" data-testid="education-cta-btn">
              Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
